import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { GetFeedAdviceResponse, UpsertFeedAdviceResponse } from './feed-advice-service.model';
import { EnergySystem, LysineSystem } from '../../settings/models/settings.model';
import {
  Consumption,
  FeedAdvice,
  FeedAdviceType,
  FeedingType,
  ParityRegistration,
  ParityTotals,
  PhaseTransitionTypes,
  ProductTotals,
  ProductTotalsSows,
  SowFeedAdviceLine,
} from '../models/feed-advice.model';

@Injectable({
  providedIn: 'root',
})
export class FeedAdviceService {
  protected baseUrl = `${this.env.API_BASE_URL}/service/pig-feed-advice/secure`;

  constructor(
    protected http: HttpClient,
    @Inject(EnvironmentInjector) private readonly env: { API_BASE_URL: string },
  ) {}

  getFeedAdvices(energySystem: EnergySystem, lysineSystem: LysineSystem): Observable<FeedAdvice[]> {
    const params = new HttpParams()
      .set('energySystem', String(energySystem))
      .set('lysineSystem', String(lysineSystem));

    return this.http.get<GetFeedAdviceResponse>(`${this.baseUrl}/feedadvice`, { params }).pipe(
      map((response) => response.feedAdvices),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(<FeedAdvice[]>[]);
        } else {
          return throwError({ message: error.message });
        }
      }),
    );
  }

  getReferenceFeedAdvices(
    country: string,
    energySystem: EnergySystem,
    lysineSystem: LysineSystem,
  ): Observable<FeedAdvice[]> {
    const params = new HttpParams()
      .set('country', country)
      .set('energySystem', String(energySystem))
      .set('lysineSystem', String(lysineSystem));

    return this.http
      .get<GetFeedAdviceResponse>(`${this.baseUrl}/feedadvice/reference`, {
        params,
      })
      .pipe(
        map((response) => response.feedAdvices),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(<FeedAdvice[]>[]);
          } else {
            return throwError({ message: error.message });
          }
        }),
      );
  }

  upsertFeedAdvice(feedadvice: FeedAdvice): Observable<UpsertFeedAdviceResponse> {
    return this.http.post<UpsertFeedAdviceResponse>(`${this.baseUrl}/feedadvice`, feedadvice).pipe(
      map((response) => response),
      catchError((error: HttpErrorResponse) => throwError({ message: error.message })),
    );
  }

  deleteFeedAdvice(feedAdviceId: number) {
    return this.http.delete(`${this.baseUrl}/feedadvice/${feedAdviceId}`);
  }

  calculateProductTotals(feedAdvice: FeedAdvice): ProductTotals[] {
    let productTotals: ProductTotals[] = [];
    if (feedAdvice) {
      productTotals = feedAdvice.phases.map((phase) => ({
        phaseNumber: phase.phaseNumber,
        productCode: phase.product?.code,
        productName: phase.product?.name,
        nettoPricePer100Kg: phase.product?.unitNettoPrice,
        totalNettoPrice: 0,
        ewKg: phase.product?.ewKg,
        totalKg: 0,
        parityRegistrations: [] as ParityRegistration[],
      }));
      switch (feedAdvice.type) {
        case FeedAdviceType.REARING:
        case FeedAdviceType.FATTENING: {
          const amountOfLines = feedAdvice.fatteningRearingFeedAdviceLines.length;
          feedAdvice.fatteningRearingFeedAdviceLines.forEach((line) => {
            line.consumptions.forEach((consumption) => {
              const factor =
                !Object.prototype.hasOwnProperty.call(line, 'correctionFactor') ||
                !line.correctionFactor ||
                feedAdvice.feedAdviceType === FeedAdviceType.REARING
                  ? line.week < 15
                    ? 1.0
                    : 0.9
                  : line.correctionFactor;
              const nextLine =
                line.week < feedAdvice.fatteningRearingFeedAdviceLines.length
                  ? feedAdvice.fatteningRearingFeedAdviceLines.find((w) => w.week === line.week + 1)
                  : null;
              let totalWholeLine: number;
              let totalProductIntakePerLineWithFactor: number;

              if (feedAdvice.feedingType === FeedingType.MANUAL) {
                const totalConsumptionForProduct =
                  line.foodPerDay * 7 * (consumption.productIntakeInPercentage / 100);

                productTotals.find((p) => p.phaseNumber === consumption.phaseNumber).totalKg +=
                  totalConsumptionForProduct;
              } else {
                switch (feedAdvice.phaseTransition) {
                  case PhaseTransitionTypes.ABRUPT: {
                    const currentLineFeedPerDay =
                      line.foodPerDay * (consumption.productIntakeInPercentage / 100);
                    totalWholeLine =
                      nextLine !== null && currentLineFeedPerDay > 0
                        ? 7 * currentLineFeedPerDay + 3 * (nextLine.foodPerDay - line.foodPerDay)
                        : 7 * currentLineFeedPerDay;
                    totalProductIntakePerLineWithFactor = totalWholeLine * factor;
                    if (totalProductIntakePerLineWithFactor > 0) {
                      productTotals.find(
                        (p) => p.phaseNumber === consumption.phaseNumber,
                      ).totalKg += totalProductIntakePerLineWithFactor;
                    }
                    break;
                  }
                  case PhaseTransitionTypes.GRADUAL: {
                    let totalWholeLineWithFactor = 0.0;
                    let nextLineFeedPerDayPerPhase: number;
                    const phase = feedAdvice.phases.find(
                      (p) => p.phaseNumber === consumption.phaseNumber,
                    );
                    const currentLineFeedPerDayPerPhase = consumption.productIntakeInPercentage
                      ? consumption.productIntakeInPercentage *
                        (line.energyValuePerDay / phase.product.ewKg)
                      : 0;
                    if (nextLine) {
                      const nextLinePercentage = nextLine.consumptions.find(
                        (c: Consumption) => c.phaseNumber === consumption.phaseNumber,
                      )?.productIntakeInPercentage;
                      nextLineFeedPerDayPerPhase = nextLinePercentage
                        ? nextLinePercentage * (nextLine.energyValuePerDay / phase.product.ewKg)
                        : 0;
                    }

                    if (line.week >= amountOfLines) {
                      totalWholeLine = 7 * currentLineFeedPerDayPerPhase;
                    } else {
                      totalWholeLine =
                        7 * currentLineFeedPerDayPerPhase +
                        3 * (nextLineFeedPerDayPerPhase - currentLineFeedPerDayPerPhase);
                    }

                    totalWholeLineWithFactor +=
                      totalWholeLine !== null ? totalWholeLine * factor : 0;
                    totalProductIntakePerLineWithFactor = totalWholeLineWithFactor / 100;

                    if (totalProductIntakePerLineWithFactor > 0) {
                      productTotals.find(
                        (p) => p.phaseNumber === consumption.phaseNumber,
                      ).totalKg += totalProductIntakePerLineWithFactor;
                    }
                    break;
                  }
                }
              }
            });
          });
          break;
        }
        case FeedAdviceType.WEANED_PIGLETS: {
          feedAdvice.weanedPigletFeedAdviceLines.forEach((line) => {
            line.consumptions.forEach((consumption) => {
              const currentLineFeedPerDay =
                line.foodPerDay * (consumption.productIntakeInPercentage / 100);
              if (currentLineFeedPerDay > 0) {
                productTotals.find((p) => p.phaseNumber === consumption.phaseNumber).totalKg +=
                  currentLineFeedPerDay / 1000;
              }
            });
          });
          break;
        }
      }

      feedAdvice.phases.forEach((x) => {
        let productTotal = productTotals.find((p) => p.phaseNumber === x.phaseNumber);
        const index = productTotals.indexOf(productTotal);

        const totalNettoPrice = +((productTotal.totalKg / 100) * x.product?.unitNettoPrice).toFixed(
          2,
        );

        productTotal = {
          ...productTotal,
          totalNettoPrice,
        };

        productTotals[index] = productTotal;
      });
    }
    return productTotals;
  }

  calculateProductTotalsSows(sowFeedAdviceLines: SowFeedAdviceLine[]): ProductTotalsSows[] {
    const productTotals: ProductTotalsSows[] = [];

    sowFeedAdviceLines.forEach((line) => {
      const index = productTotals.findIndex((pt) => pt.productCode === line.product?.code);
      if (index >= 0) {
        productTotals[index] = {
          ...productTotals[index],
          parityTotals: this.aggregateParityTotals(
            line.parityRegistrations,
            line.dayTo - line.dayFrom + 1,
            productTotals[index]?.parityTotals,
          ),
        };
      } else {
        productTotals.push({
          productCode: line.product?.code,
          productName: line.product?.name,
          unitNettoPrice: line.product?.unitNettoPrice,
          parityTotals: this.aggregateParityTotals(
            line.parityRegistrations,
            line.dayTo - line.dayFrom + 1,
            productTotals[index]?.parityTotals,
          ),
        });
      }
    });

    return productTotals;
  }

  private aggregateParityTotals(
    parityRegistrations: ParityRegistration[],
    numberOfDays: number,
    parityTotals: ParityTotals[],
  ) {
    if (!parityTotals) {
      parityTotals = [];
    }
    parityRegistrations.forEach((pr) => {
      const index = parityTotals.findIndex((pt) => pt.parity === pr.parity);
      if (index >= 0) {
        parityTotals[index] = {
          ...parityTotals[index],
          feedKg: parityTotals[index].feedKg + pr.feedKg * numberOfDays,
        };
      } else {
        parityTotals.push({
          parity: pr.parity,
          feedKg: pr.feedKg * numberOfDays,
        });
      }
    });

    return parityTotals;
  }
}
